<template>
  <div class="stickerBasicForm">
    <div class="stickerBasicForm__metaLine">
      <div class="stickerBasicForm__type">
        <span class="stickerBasicForm__label">stickers type:</span>
        <span>{{ typeLabel }}</span>
      </div>
      <div class="stickerBasicForm__designer">
        <span class="stickerBasicForm__label">设计师</span>
        <el-select
          v-model="authorId"
          :disabled="initializationLocked"
          clearable
          filterable
          placeholder="可以不选"
          @change="onAuthorChange"
        >
          <el-option
            v-for="item in designerOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </div>
    <p v-if="initializationLocked" class="stickerBasicForm__note">{{ initializationMessage }}</p>
    <p v-if="initializationLocked && initializationError" class="stickerBasicForm__note">最近失败原因：{{ initializationError }}</p>
    <p v-if="initializationLocked && progress.total" class="stickerBasicForm__note">AI emoji 已完成 {{ progress.done }} / {{ progress.total }} 张。</p>
    <p v-if="packTagText" class="stickerBasicForm__note">整包：{{ packTagText }}</p>
    <p v-for="task in activeTasks" :key="task.id" class="stickerBasicForm__note">{{ taskText(task) }}</p>
    <el-button v-if="canRetryInitialization" size="small" :loading="retrying" @click="retryInitialization">重试初始化</el-button>
    <el-button v-if="canRetryInitialization" size="small" :loading="abandoning" @click="abandonInitialization">放弃本次初始化</el-button>
    <sticker-strip
      :cards="cards"
      :replaceable="!!ruleForm.id && !initializationLocked"
      :replacing="replacing"
      @replace="replaceImage"
      @emoji="onEmoji"
      @reorder="onReorder"
      @choose-tray="onChooseTray"
    />
  </div>
</template>

<script>
import { stickerInitializationLocked, stickerEmojiProgress, stickerInitializationMessage, stickerInitializationError, stickerEditZipUrl } from '../stickerInitialization'
import basicFormMixin from './basicFormMixin'
import StickerStrip from './StickerStrip.vue'
import { coverUrl, stickerCards, stickerTypeLabel } from '../stickerExtaCards'
import { chooseTray, defaultTrayId, reorderStickers, usableAsTray } from '../stickerOrder'
import { getAllDesignerOptionCreate } from '../../../../../api/comment'
import { abandonGenericResourceStickerInitialization, getGenericResourceById, listStickerAiTasks, listStickerTags, replaceGenericResourceSticker, retryGenericResourceStickerInitialization } from '../../../../../api/genericResource'

// 快照用于区分本地编辑和后台更新，换图时也按稳定 id 保留编辑。
const copy = (value) => value == null ? value : JSON.parse(JSON.stringify(value))
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right)
const stickerKey = (item) => item && (item.id || item.file)
const trayKey = (exta) => exta.trayStickerId || null

function supersededTask(task) {
  try {
    const payload = typeof task.payload === 'string' ? JSON.parse(task.payload) : task.payload
    return !!(payload && payload.superseded)
  } catch (error) {
    return false
  }
}

function mergeStickerConfig(base, local, remote) {
  const merged = { ...remote }
  Object.keys(local || {}).forEach((key) => {
    if (!['stickers', 'trayStickerId'].includes(key) && !same(local[key], base[key])) merged[key] = copy(local[key])
  })
  const baseItems = new Map((base.stickers || []).map((item) => [stickerKey(item), item]))
  const localItems = new Map((local.stickers || []).map((item) => [stickerKey(item), item]))
  let conflict = false
  merged.stickers = (remote.stickers || []).map((item) => {
    const previous = baseItems.get(stickerKey(item))
    const draft = localItems.get(stickerKey(item))
    if (previous && draft && !same(previous.emojis, draft.emojis)) {
      conflict = conflict || previous.file !== item.file || base.resourceUrlPrefix !== remote.resourceUrlPrefix
      return { ...item, emojis: copy(draft.emojis) }
    }
    return copy(item)
  })
  // 拖动按 id 比较顺序，避免索引变化后把本地排序套到另一张贴纸上。
  if (!same((base.stickers || []).map(stickerKey), (local.stickers || []).map(stickerKey))) {
    const rank = new Map((local.stickers || []).map((item, index) => [stickerKey(item), index]))
    merged.stickers.sort((a, b) => (rank.has(stickerKey(a)) ? rank.get(stickerKey(a)) : Infinity) - (rank.has(stickerKey(b)) ? rank.get(stickerKey(b)) : Infinity))
  }
  merged.trayStickerId = trayKey(local) !== trayKey(base) ? trayKey(local) : trayKey(remote)
  return { merged, conflict }
}

export default {
  name: 'StickerBasicForm',
  components: { StickerStrip },
  mixins: [basicFormMixin],
  props: {
    ruleForm: { type: Object, required: true },
  },
  data() {
    return {
      designerOptions: [],
      authorId: this.ruleForm.authorId || '',
      replacing: false,
      retrying: false,
      abandoning: false,
      tasks: [],
      tagBindings: [],
      taskTimer: null,
      recognitionRequest: null,
      refreshedTasks: [],
      recognitionDisposed: false,
      recognitionEpoch: 0,
      stickerRequestVersion: 0,
      serverExta: copy((this.ruleForm.jsonConfig && this.ruleForm.jsonConfig.exta) || {}),
    }
  },
  computed: {
    initializationLocked() { return stickerInitializationLocked(this.ruleForm) },
    canRetryInitialization() { return this.initializationLocked && [0, 11].includes(Number(this.ruleForm.onOrOff)) },
    progress() { return stickerEmojiProgress(this.ruleForm) },
    initializationMessage() { return stickerInitializationMessage(this.ruleForm) },
    initializationError() { return stickerInitializationError(this.ruleForm) },
    exta() {
      return (this.ruleForm.jsonConfig && this.ruleForm.jsonConfig.exta) || null
    },
    cards() {
      const byItem = {}
      this.tagBindings.forEach((tag) => {
        const id = tag.itemId || ''
        if (!byItem[id]) {
          byItem[id] = []
        }
        byItem[id].push(tag)
      })
      return stickerCards(this.exta).map((card) => ({
        ...card,
        tags: byItem[card.id] || [],
      }))
    },
    packTagText() {
      return this.tagBindings.filter((tag) => !tag.itemId).map((tag) => {
        const born = tag.origin === 2 ? '模型创建' : '人工创建'
        const bound = tag.binder === 2 ? '模型绑上' : '人绑上'
        return `${tag.name}（${born}，${bound}）`
      }).join('、')
    },
    activeTasks() {
      return this.tasks.filter((task) => !supersededTask(task) && [0, 1, 3].includes(task.state))
    },
    typeLabel() {
      return stickerTypeLabel(this.exta)
    },
  },
  watch: {
    'ruleForm.id'() {
      this.recognitionEpoch++
      this.stickerRequestVersion++
      this.recognitionRequest = null
      this.tasks = []
      this.refreshedTasks = []
      this.tagBindings = []
      this.serverExta = copy(this.exta || {})
      this.loadRecognition()
    },
    exta: {
      immediate: true,
      handler() {
        this.syncCover()
      },
    },
    'exta.trayStickerId'() {
      this.syncCover()
    },
  },
  created() {
    this.loadRecognition()
    this.scheduleRecognitionPoll()
    getAllDesignerOptionCreate().then((res) => {
      this.designerOptions = (res && res.data) || []
    }).catch(() => {
      this.designerOptions = []
    })
  },
  beforeDestroy() {
    this.recognitionDisposed = true
    this.recognitionEpoch++
    this.stickerRequestVersion++
    if (this.taskTimer) {
      clearTimeout(this.taskTimer)
      this.taskTimer = null
    }
  },
  methods: {
    taskText(task) {
      const kind = task.kind === 2 ? '整包标签' : '换图'
      const state = task.state === 0 ? '排队' : task.state === 1 ? '识别中' : '失败'
      return `${kind}${state}${task.lastError ? `：${task.lastError}` : ''}`
    },
    // 有排队/识别中/失败待看或初始化锁着时 3s；空闲时 15s，少打无用接口。
    recognitionPollDelay() {
      const busy = (this.tasks || []).some((task) => !supersededTask(task) && [0, 1, 3].includes(task.state))
      if (busy || stickerInitializationLocked(this.ruleForm)) return 3000
      return 15000
    },
    scheduleRecognitionPoll() {
      if (this.taskTimer) {
        clearTimeout(this.taskTimer)
        this.taskTimer = null
      }
      if (this.recognitionDisposed) return
      this.taskTimer = setTimeout(() => {
        this.loadRecognition().finally(() => this.scheduleRecognitionPoll())
      }, this.recognitionPollDelay())
    },
    recognitionContext() {
      return { id: this.ruleForm.id, form: this.ruleForm, epoch: this.recognitionEpoch }
    },
    recognitionCurrent(context) {
      return !this.recognitionDisposed && context.id === this.ruleForm.id && context.form === this.ruleForm && context.epoch === this.recognitionEpoch
    },
    loadRecognition() {
      if (!this.ruleForm.id || this.recognitionDisposed) return Promise.resolve()
      const context = this.recognitionContext()
      if (this.recognitionRequest && this.recognitionCurrent(this.recognitionRequest.context)) return this.recognitionRequest.promise
      const request = { context }
      // 一个批次包含资源刷新，完成前不再发起轮询，以免旧响应回写。
      request.promise = Promise.all([
        listStickerAiTasks(context.id).then(async (res) => {
          if (!this.recognitionCurrent(context)) return
          const tasks = (res && res.data) || []
          const relevant = tasks.filter((task) => !supersededTask(task))
          const changed = relevant.some((task) => !same(task, this.refreshedTasks.find((previous) => previous.id === task.id)))
          this.tasks = tasks
          if ((changed || stickerInitializationLocked(this.ruleForm)) && await this.reloadSticker() && this.recognitionCurrent(context)) {
            this.refreshedTasks = copy(relevant)
          }
        }).catch(() => {
          if (this.recognitionCurrent(context) && stickerInitializationLocked(this.ruleForm)) return this.reloadSticker()
        }),
        listStickerTags(context.id).then((res) => {
          if (!this.recognitionCurrent(context)) return
          const next = (res && res.data) || []
          // 内容没变不换数组，否则 cards 重算会打断贴纸条上正在进行的拖拽。
          if (!same(next, this.tagBindings)) this.tagBindings = next
        }).catch(() => {}),
      ]).finally(() => {
        if (this.recognitionRequest === request) this.recognitionRequest = null
      })
      this.recognitionRequest = request
      return request.promise
    },
    applyStickerExta(exta) {
      const { merged, conflict } = mergeStickerConfig(this.serverExta || {}, this.exta || {}, exta)
      this.serverExta = copy(exta)
      this.$set(this.ruleForm.jsonConfig, 'exta', merged)
      this.syncCover()
      if (conflict) this.$message.warning('贴纸图片已更新，已保留你编辑的 emoji，请确认它们仍适合新图后再保存')
    },
    reloadSticker() {
      const context = this.recognitionContext()
      const version = ++this.stickerRequestVersion
      const wasInitializing = stickerInitializationLocked(this.ruleForm)
      const previousVisibility = this.ruleForm.onOrOff
      return getGenericResourceById({ id: context.id }).then((res) => {
        if (!this.recognitionCurrent(context) || version !== this.stickerRequestVersion) return
        const data = res && res.data
        if (!data || !data.jsonConfig) return
        // 初始化事实由服务端维护；只同步这些字段，保留 URL 和其他未保存配置。
        const initializationKeys = ['stickerProcessing', 'stickerPackError']
        initializationKeys.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(data.jsonConfig, key)) {
            this.$set(this.ruleForm.jsonConfig, key, copy(data.jsonConfig[key]))
          } else {
            this.$delete(this.ruleForm.jsonConfig, key)
          }
        })
        if (wasInitializing && this.ruleForm.onOrOff === previousVisibility && data.onOrOff != null) {
          this.$set(this.ruleForm, 'onOrOff', data.onOrOff)
        }
        if (data.jsonConfig.exta) this.applyStickerExta(data.jsonConfig.exta)
        return true
      }).catch(() => false)
    },
    async retryInitialization() {
      this.retrying = true
      try {
        const result = await retryGenericResourceStickerInitialization(this.ruleForm.id)
        if (!result || result.success === false || result.errCode) throw new Error((result && result.errMessage) || '重试提交失败')
        this.$set(this.ruleForm, 'onOrOff', 12)
        this.$message.success('已提交重试，稍后刷新查看进度')
      } catch (error) { this.$message.error(error.message || '重试提交失败') }
      finally { this.retrying = false }
    },
    async abandonInitialization() {
      try {
        await this.$confirm('放弃后恢复换包前的贴纸和上下线状态，这次上传的 zip 会被删除。是否继续？', '放弃本次初始化', { type: 'warning' })
      } catch (cancelled) { return }
      this.abandoning = true
      try {
        const result = await abandonGenericResourceStickerInitialization(this.ruleForm.id)
        if (!result || result.success === false || result.errCode) throw new Error((result && result.errMessage) || '放弃失败')
        const res = await getGenericResourceById({ id: this.ruleForm.id })
        const data = res && res.data
        if (data && data.jsonConfig) {
          this.$delete(this.ruleForm.jsonConfig, 'stickerProcessing')
          // 放弃后回到上一次处理完成的包；它的地址要带回表单，否则保存又被「请上传资源文件」拦住。
          this.$set(this.ruleForm.jsonConfig, 'url', stickerEditZipUrl(data.jsonConfig))
          this.$set(this.ruleForm, 'onOrOff', data.onOrOff)
          if (data.jsonConfig.exta) this.applyStickerExta(data.jsonConfig.exta)
        }
        this.$message.success('已放弃本次初始化')
      } catch (error) { this.$message.error(error.message || '放弃失败') }
      finally { this.abandoning = false }
    },
    captureSnapshot() {
      return { stickerReady: true }
    },
    onAuthorChange(value) {
      this.$set(this.ruleForm, 'authorId', value || null)
    },
    onReorder(from, to) {
      if (!this.exta) {
        return
      }
      reorderStickers(this.exta, from, to)
    },
    onChooseTray(index) {
      if (!chooseTray(this.exta, index)) {
        return
      }
      this.$set(this.exta, 'trayStickerId', this.exta.stickers[index].id)
      this.syncCover()
    },
    syncCover() {
      if (!this.exta) return
      const stickers = Array.isArray(this.exta.stickers) ? this.exta.stickers : []
      const selected = stickers.find((item) => item && item.id === this.exta.trayStickerId)
      // 运营选的那张还在就保留，合格与否都行；失效时按默认规则补上。
      if (!usableAsTray(selected)) {
        this.$set(this.exta, 'trayStickerId', defaultTrayId(stickers))
      }
      this.$set(this.ruleForm, 'thumbUrl', coverUrl(this.exta))
    },
    onEmoji(item, parts) {
      if (!this.exta || item.stickerIndex == null) {
        return
      }
      const sticker = this.exta.stickers && this.exta.stickers[item.stickerIndex]
      if (!sticker) {
        return
      }
      this.$set(sticker, 'emojis', parts)
    },
    replaceImage(fileName, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || !this.ruleForm.id || !fileName) {
        return
      }
      const context = this.recognitionContext()
      this.replacing = true
      return replaceGenericResourceSticker({
        id: this.ruleForm.id,
        fileName,
        file,
      }).then((res) => {
        const exta = res && res.data
        if (!this.recognitionCurrent(context) || !exta || !this.ruleForm.jsonConfig) {
          return
        }
        const previous = (this.exta.stickers || []).find((item) => item.file === fileName)
        this.stickerRequestVersion++
        this.applyStickerExta(exta)
        const stillThere = (exta.stickers || []).some((item) => previous && item.file === previous.file)
        if (stillThere) {
          this.$message.success('新图已排队，认完才会给客人看')
        }
        this.loadRecognition()
      }).finally(() => {
        if (this.recognitionCurrent(context)) this.replacing = false
      })
    },
  },
}
</script>

<style scoped>
.stickerBasicForm__metaLine {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 40px;
}
.stickerBasicForm__type,
.stickerBasicForm__designer {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stickerBasicForm__label {
  color: #4a4541;
  white-space: nowrap;
}
.stickerBasicForm__designer .el-select {
  width: 280px;
}
.stickerBasicForm__note {
  margin: 8px 0 0;
  color: #57534e;
}
</style>
